import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { motion, AnimatePresence } from "framer-motion"
import {
    Search,
    Book,
    Video,
    MessageCircle,
    ChevronLeft,
    ChevronRight,
    Clock,
    Users,
    Zap,
    HelpCircle,
    X,
    Send,
    UserPenIcon,
    DollarSignIcon,
    FileQuestionMarkIcon,
    FileText,
    Wrench,
} from "lucide-react"
import { SITE_URL, PAGE_OG_IMAGES } from "../seo/constants"
import { generateMetadata } from "../seo/metadataGenerator"
import { generateBreadcrumbSchema, generateFAQPageSchema, generateGraphSchema, generateWebPageSchema } from "../seo/schemaGenerator"
import { ReactComponent as MarketingManagerIcon } from '../assets/images/icons/growth-engine.svg';
import { ReactComponent as ArtistIcon } from '../assets/images/icons/artist-manager.svg'
import { ReactComponent as AudioIcon } from '../assets/images/icons/urstudio.svg';
import { ReactComponent as CreatorIcon } from '../assets/images/icons/content-creator.svg';
import { ReactComponent as AnalystIcon } from '../assets/images/icons/data-analyst.svg';
import { ReactComponent as ProfessorUIcon } from '../assets/images/icons/professoru.svg';
import { ReactComponent as AmplitudeIcon } from '../assets/images/icons/amplitude.svg';
import { ReactComponent as PublicistIcon } from '../assets/images/icons/publicist-new.svg';
import { ReactComponent as DistributionIcon } from '../assets/images/icons/distribution-new.svg';
import SeoHead from "../seo/SeoHead"
import { usePrerenderReady } from "../hooks/usePrerenderReady";
import { useContents, useGuideSearch, useModules } from "../queries/professoru/contents.api";
import HelpGuideViewer from "../components/HelpCenter/HelpGuideViewer";
import MusicLoader from "../helper/MusicLoader";
import { useSendSupportMessage } from "../queries/contact.query";

const GUIDE_TYPES = ["guide", "walkthroughs", "walkthrough"]
const ALL_TOPICS_PAGE_SIZE = 5
const DISABLED_MODULE_LABELS = ["Publicist", "Content Creator"]
const DEMO_GUIDE = {
    id: "demo-video",
    title: "Why We Built UpNComer",
    description: "See UpNComer in action",
    mediaUrl: "https://www.youtube.com/watch?v=ElPcG2wTSKw",
    moduleName: "Demo",
}

const hasModuleName = (item) => {
    const name = item?.moduleName ?? item?.moduleTitle
    return name !== null && name !== undefined && String(name).trim() !== ""
}

const CONTENT_TYPE_TAGS = new Set(["lesson", "guide", "walkthrough", "walkthroughs", "interview", "troubleshooting"])

const normalizeTags = (tags = []) =>
    (Array.isArray(tags) ? tags : [tags])
        .map((tag) => String(tag || "").trim())
        .filter(Boolean)

const formatTagLabel = (tag = "") =>
    String(tag)
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")

const getGuideLabels = (guide) => {
    const tags = normalizeTags(guide?.tags)
    const typeTags = tags.filter((tag) => CONTENT_TYPE_TAGS.has(tag.toLowerCase()))
    const otherTags = tags.filter((tag) => !CONTENT_TYPE_TAGS.has(tag.toLowerCase()))
    const ordered = [...otherTags, ...typeTags]
    if (ordered.length) return ordered.map(formatTagLabel)
    return guide?.moduleName ? [formatTagLabel(guide.moduleName)] : []
}

const guideHasTag = (guide, tag) =>
    (guide.tags || []).some((value) => value.toLowerCase() === String(tag).toLowerCase())

const matchesModuleLabel = (guide, label) => {
    const normalizedLabel = normalizeModuleTitle(label)
    return (
        normalizeModuleTitle(guide.moduleName) === normalizedLabel
        || guideHasTag(guide, label)
        || guideHasTag(guide, normalizedLabel)
    )
}

const mapContentToGuide = (item, fallbackCategory = "") => {
    const moduleName = normalizeModuleTitle(item.moduleName || item.moduleTitle || fallbackCategory)
    return {
        id: item._id,
        title: item.title,
        description: item.description || item.content?.description || item.subtitle || "",
        category: fallbackCategory || item.moduleId || slugify(moduleName),
        moduleId: item.moduleId,
        moduleName,
        mediaUrl: item.content?.mediaUrl || "",
        thumbnail: item.thumbnail?.url || item.thumbnail?.externalUrl || "",
        type: item.type,
        tags: normalizeTags(item.tags),
    }
}

const MODULE_ICON_MAP = {
    "Growth Engine": MarketingManagerIcon,
    "Publicist": PublicistIcon,
    "UrStudio": AudioIcon,
    "Audio Engineer": AudioIcon,
    "Data Analyst": AnalystIcon,
    "Amplitude AI": AmplitudeIcon,
    "Artist Manager": ArtistIcon,
    "Artsist Manager": ArtistIcon,
    "ProfessorU": ProfessorUIcon,
    "Content Creator": CreatorIcon,
    "Distribution": DistributionIcon,
}

const normalizeModuleTitle = (title = "") =>
    title === "Artsist Manager" ? "Artist Manager" : title

const slugify = (value = "") =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")

const isModuleGuide = (item) => GUIDE_TYPES.includes(item?.type)

const STATIC_CATEGORIES = [
    { id: "billing", label: "Billing & Pricing", icon: DollarSignIcon },
    { id: "settings", label: "Account & Settings", icon: UserPenIcon },
    { id: "teams", label: "Teams", icon: Users },
    { id: "troubleshooting", label: "Troubleshooting", icon: FileQuestionMarkIcon },
]

const DEFAULT_SUPPORT_MESSAGE = "Hi! I'm here to help you with UpNComer. What can I assist you with today?"

const SUPPORT_TOPICS = [
    { value: "general", label: "General Inquiry" },
    { value: "demo", label: "Request Demo" },
    { value: "pricing", label: "Pricing Questions" },
    { value: "technical", label: "Technical Support" },
    { value: "partnership", label: "Partnership Opportunities" },
    { value: "other", label: "Other" },
]

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const getSupportUserName = (userDetails = {}, guestContact = {}, useGuestContact = false) => {
    const source = useGuestContact ? guestContact : userDetails
    const fullName = [source.firstName, source.lastName]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ")
    if (fullName) return fullName
    if (useGuestContact) return "UpNComer User"
    return userDetails.userName?.trim() || userDetails.email?.split("@")[0] || "UpNComer User"
}

const getMessageTime = () =>
    new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })

const createChatMessage = (role, text, extras = {}) => ({
    id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role,
    text,
    time: getMessageTime(),
    ...extras,
})

export default function HelpCenterPage() {
    usePrerenderReady();
    const userDetails = useSelector((state) => state.auth.userDetails)
    const isSignedIn = Boolean(localStorage.getItem("authToken"))
    const sendSupport = useSendSupportMessage()
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedCategory, setSelectedCategory] = useState("all")
    const [selectedGuideId, setSelectedGuideId] = useState(null)
    const [allTopicsPage, setAllTopicsPage] = useState(0)
    const [searchPage, setSearchPage] = useState(0)
    const [showSupportChat, setShowSupportChat] = useState(false)
    const [chatMessage, setChatMessage] = useState("")
    const [chatMessages, setChatMessages] = useState(() => [
        createChatMessage("support", DEFAULT_SUPPORT_MESSAGE),
    ])
    const [chatStep, setChatStep] = useState("greeting")
    const [selectedTopic, setSelectedTopic] = useState(null)
    const [customTopic, setCustomTopic] = useState("")
    const [guestContact, setGuestContact] = useState({ firstName: "", lastName: "", email: "" })
    const [contactError, setContactError] = useState("")
    const chatEndRef = useRef(null)
    const [cursorPosition, setCursorPosition] = useState({ x: -100, y: -100 });
    const [isHovering, setIsHovering] = useState(false);

    const { data: modules, isPending: modulesPending } = useModules()
    const { data: contentsResponse, isPending: contentsPending } = useContents({
        type: ["guide_and_troubleshooting"],
        page: 1,
        limit: 200,
    })

    const [debouncedSearch, setDebouncedSearch] = useState("")

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400)
        return () => clearTimeout(timer)
    }, [searchQuery])

    const isSearching = debouncedSearch.length > 0
    const searchModuleId = (
        selectedCategory !== "all"
        && selectedCategory !== "troubleshooting"
        && selectedCategory !== "billing"
        && selectedCategory !== "settings"
        && selectedCategory !== "teams"
    ) ? selectedCategory : undefined

    const { data: searchResponse, isPending: searchPending, isFetching: searchFetching } = useGuideSearch(
        {
            search: debouncedSearch,
            page: searchPage + 1,
            limit: ALL_TOPICS_PAGE_SIZE,
            ...(searchModuleId ? { moduleId: searchModuleId } : {}),
        },
        { enabled: isSearching }
    )

    const updateCursorPosition = useCallback((e) => {
        requestAnimationFrame(() => {
            setCursorPosition({ x: e.clientX, y: e.clientY });
        });
    }, []);

    useEffect(() => {
        document.body.style.cursor = "none";
        const handleMouseEnter = () => setIsHovering(true);
        const handleMouseLeave = () => setIsHovering(false);

        const interactiveElements = document.querySelectorAll(
            'a, button, input, [role="button"]'
        );
        interactiveElements.forEach((element) => {
            element.addEventListener("mouseenter", handleMouseEnter);
            element.addEventListener("mouseleave", handleMouseLeave);
        });

        window.addEventListener("mousemove", updateCursorPosition);

        // Simulate loading time (remove this in production and use real loading logic)
        // const timer = setTimeout(() => {
        //     setIsLoading(false);
        // }, 3000);

        return () => {
            // clearTimeout(timer);
            document.body.style.cursor = "auto";
            window.removeEventListener("mousemove", updateCursorPosition);
            interactiveElements.forEach((element) => {
                element.removeEventListener("mouseenter", handleMouseEnter);
                element.removeEventListener("mouseleave", handleMouseLeave);
            });
        };
    }, [updateCursorPosition]);

    useEffect(() => {
        if (!showSupportChat) return
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [chatMessages, showSupportChat, chatStep])

    const resetSupportChat = useCallback(() => {
        setChatMessages([createChatMessage("support", DEFAULT_SUPPORT_MESSAGE)])
        setChatStep("greeting")
        setSelectedTopic(null)
        setCustomTopic("")
        setGuestContact({ firstName: "", lastName: "", email: "" })
        setContactError("")
        setChatMessage("")
    }, [])

    const closeSupportChat = useCallback(() => {
        setShowSupportChat(false)
        resetSupportChat()
    }, [resetSupportChat])

    const openSupportChat = useCallback(() => {
        resetSupportChat()
        setShowSupportChat(true)
    }, [resetSupportChat])

    const addChatMessages = useCallback((messages) => {
        setChatMessages((prev) => [...prev, ...messages])
    }, [])

    const handleTopicSelect = (topic) => {
        if (chatStep !== "topic") return

        setSelectedTopic(topic)
        addChatMessages([createChatMessage("user", topic.label)])

        if (topic.value === "other") {
            setChatStep("custom-topic")
            addChatMessages([
                createChatMessage("support", "Got it. What topic should we file this under?"),
            ])
            return
        }

        setChatStep("query")
        addChatMessages([
            createChatMessage(
                "support",
                `Thanks. Please write the message or query you'd like our team to review about ${topic.label}.`
            ),
        ])
    }

    const promptTopicSelection = () => {
        setChatStep("topic")
        addChatMessages([
            createChatMessage(
                "support",
                "Please select a topic so we can route this to the right person.",
                { showTopics: true }
            ),
        ])
    }

    const handleGuestContactSubmit = () => {
        if (chatStep !== "guest-contact") return

        const firstName = guestContact.firstName.trim()
        const lastName = guestContact.lastName.trim()
        const email = guestContact.email.trim()

        if (!firstName || !lastName || !email) {
            setContactError("Please enter your first name, last name, and email.")
            return
        }
        if (!EMAIL_PATTERN.test(email)) {
            setContactError("Please enter a valid email address.")
            return
        }

        setContactError("")
        addChatMessages([
            createChatMessage("user", `${firstName} ${lastName} · ${email}`),
        ])
        promptTopicSelection()
    }

    const handleSendChatMessage = async () => {
        const trimmed = chatMessage.trim()
        if (!trimmed || sendSupport.isPending) return

        if (chatStep === "greeting") {
            addChatMessages([createChatMessage("user", trimmed)])
            setChatMessage("")
            if (!isSignedIn) {
                setChatStep("guest-contact")
                addChatMessages([
                    createChatMessage(
                        "support",
                        "Before we continue, please share your first name, last name, and email so our team can follow up.",
                        { showGuestForm: true }
                    ),
                ])
                return
            }
            promptTopicSelection()
            return
        }

        if (chatStep === "custom-topic") {
            setCustomTopic(trimmed)
            addChatMessages([createChatMessage("user", trimmed)])
            setChatMessage("")
            setChatStep("query")
            addChatMessages([
                createChatMessage(
                    "support",
                    `Thanks. Please write the message or query you'd like our team to review about ${trimmed}.`
                ),
            ])
            return
        }

        if (chatStep !== "query") return

        const topicValue = selectedTopic?.value === "other"
            ? customTopic.trim()
            : selectedTopic?.value
        const topicLabel = selectedTopic?.value === "other"
            ? customTopic.trim()
            : selectedTopic?.label

        if (!topicValue) return

        const supportName = getSupportUserName(userDetails, guestContact, !isSignedIn)
        const supportEmail = isSignedIn
            ? (userDetails?.email || "")
            : (guestContact.email || "").trim()

        addChatMessages([createChatMessage("user", trimmed)])
        setChatMessage("")

        try {
            await sendSupport.mutateAsync({
                name: supportName,
                email: supportEmail,
                topic: topicLabel,
                message: trimmed,
            })
            setChatStep("complete")
            addChatMessages([
                createChatMessage(
                    "support",
                    `Thanks for reaching out about ${topicLabel}. Your request is with the UpNComer Team, and we'll email you at ${supportEmail || "the email you provided"} with an update shortly.`
                ),
            ])
        } catch {
            addChatMessages([
                createChatMessage(
                    "support",
                    "We couldn't send that just now. Please try again in a moment, or email support@upncomer.co."
                ),
            ])
        }
    }

    const moduleGuides = useMemo(() => {
        const seen = new Set()
        return (contentsResponse?.data || [])
            .filter(isModuleGuide)
            .filter(hasModuleName)
            .filter((item) => {
                if (!item?._id || seen.has(item._id)) return false
                seen.add(item._id)
                return true
            })
            .map((item) => mapContentToGuide(item))
    }, [contentsResponse])

    const troubleshootingGuides = useMemo(() => {
        return (contentsResponse?.data || [])
            .filter((item) => item?._id && item.type === "troubleshooting")
            .map((item) => mapContentToGuide(item, "troubleshooting"))
    }, [contentsResponse])

    const allGuides = useMemo(
        () => [...moduleGuides, ...troubleshootingGuides],
        [moduleGuides, troubleshootingGuides]
    )

    const moduleCategories = useMemo(() => {
        return (modules || [])
            .filter((item) => item.title)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((item) => {
                const label = normalizeModuleTitle(item.title)
                const id = item._id || slugify(label)
                const isDisabled = DISABLED_MODULE_LABELS.includes(label)
                const Icon = MODULE_ICON_MAP[item.title] || MODULE_ICON_MAP[label] || ProfessorUIcon
                return {
                    id,
                    label,
                    icon: <Icon className="w-5 h-5" />,
                    count: isDisabled ? 0 : allGuides.filter((guide) => matchesModuleLabel(guide, label)).length,
                    disabled: isDisabled,
                }
            })
    }, [modules, allGuides])

    const categories = useMemo(() => {
        const staticWithCounts = STATIC_CATEGORIES.map((category) => {
            const Icon = category.icon
            const count = category.id === "troubleshooting"
                ? troubleshootingGuides.length
                : category.id === "settings"
                    ? allGuides.filter((guide) => guideHasTag(guide, "Account")).length
                    : category.id === "teams" || category.id === "billing"
                        ? allGuides.filter((guide) => guideHasTag(guide, "Teams")).length
                        : allGuides.filter((guide) => guide.category === category.id).length
            return {
                ...category,
                icon: <Icon className="w-5 h-5" />,
                count,
            }
        })

        return [
            {
                id: "all",
                label: "All Topics",
                icon: <Book className="w-5 h-5" />,
                count: allGuides.length,
            },
            ...moduleCategories,
            ...staticWithCounts,
        ]
    }, [allGuides, moduleCategories, troubleshootingGuides])

    const categoryGuides = useMemo(() => allGuides.filter((guide) => {
        if (selectedCategory === "all") return true
        if (selectedCategory === "troubleshooting") return guide.category === "troubleshooting"
        if (selectedCategory === "settings") return guideHasTag(guide, "Account")
        if (selectedCategory === "teams" || selectedCategory === "billing") return guideHasTag(guide, "Teams")
        const selectedModule = (modules || []).find((item) => item._id === selectedCategory)
        const selectedLabel = selectedModule ? normalizeModuleTitle(selectedModule.title) : ""
        return guide.moduleId === selectedCategory || (selectedLabel && matchesModuleLabel(guide, selectedLabel))
    }), [allGuides, modules, selectedCategory])

    const searchedGuides = useMemo(() => {
        const results = Array.isArray(searchResponse)
            ? searchResponse
            : (searchResponse?.data || [])

        return results
            .filter((item) => item?._id || item?.id || item?.title)
            .filter((item) => {
                if (selectedCategory === "troubleshooting") return item.type === "troubleshooting"
                if (selectedCategory === "settings") return guideHasTag(item, "Account")
                if (selectedCategory === "teams" || selectedCategory === "billing") return guideHasTag(item, "Teams")
                return true
            })
            .map((item) => mapContentToGuide(item, item.type === "troubleshooting" ? "troubleshooting" : ""))
    }, [searchResponse, selectedCategory])

    const filteredGuides = categoryGuides

    const isAllTopics = selectedCategory === "all"
    const isPaginatedCategory = isAllTopics || selectedCategory === "troubleshooting"
    const searchTotal = searchResponse?.pagination?.total ?? searchedGuides.length
    const searchPageCount = Math.max(
        1,
        searchResponse?.pagination?.totalPages || Math.ceil(searchTotal / ALL_TOPICS_PAGE_SIZE) || 1
    )
    const allTopicsPageCount = Math.max(1, Math.ceil(filteredGuides.length / ALL_TOPICS_PAGE_SIZE))
    const currentAllTopicsPage = Math.min(allTopicsPage, allTopicsPageCount - 1)
    const currentSearchPage = Math.min(searchPage, searchPageCount - 1)
    const listedGuides = isPaginatedCategory
        ? filteredGuides.slice(
            currentAllTopicsPage * ALL_TOPICS_PAGE_SIZE,
            currentAllTopicsPage * ALL_TOPICS_PAGE_SIZE + ALL_TOPICS_PAGE_SIZE
        )
        : filteredGuides

    const isDemoGuide = selectedGuideId === DEMO_GUIDE.id
    const selectedGuide = allGuides.find((guide) => guide.id === selectedGuideId)
        || searchedGuides.find((guide) => guide.id === selectedGuideId)
        || (isDemoGuide ? DEMO_GUIDE : null)

    const viewerGuides = useMemo(() => {
        if (isDemoGuide) return [DEMO_GUIDE]
        if (searchedGuides.some((guide) => guide.id === selectedGuideId)) return searchedGuides
        if (isPaginatedCategory) return listedGuides
        return categoryGuides
    }, [categoryGuides, isDemoGuide, isPaginatedCategory, listedGuides, searchedGuides, selectedGuideId])
    const isGuidesLoading = modulesPending || contentsPending
    const isSearchLoading = searchPending || searchFetching

    useEffect(() => {
        if (!selectedGuideId || selectedGuideId === DEMO_GUIDE.id) return
        const existsInBrowse = filteredGuides.some((guide) => guide.id === selectedGuideId)
        const existsInSearch = searchedGuides.some((guide) => guide.id === selectedGuideId)
        if (!existsInBrowse && !existsInSearch) {
            setSelectedGuideId(null)
        }
    }, [filteredGuides, searchedGuides, selectedGuideId])

    useEffect(() => {
        setAllTopicsPage(0)
        setSearchPage(0)
    }, [searchQuery, selectedCategory])

    useEffect(() => {
        if (allTopicsPage > allTopicsPageCount - 1) {
            setAllTopicsPage(Math.max(0, allTopicsPageCount - 1))
        }
        if (searchPage > searchPageCount - 1) {
            setSearchPage(Math.max(0, searchPageCount - 1))
        }
    }, [allTopicsPage, allTopicsPageCount])

    const handleCategorySelect = (categoryId, disabled) => {
        if (disabled) return
        setSelectedCategory(categoryId)
        setSelectedGuideId(null)
        setAllTopicsPage(0)
    }

    const handleGuideSelect = (guide) => {
        setSelectedGuideId(guide.id)
    }

    const handleWatchDemo = () => {
        setSelectedGuideId(DEMO_GUIDE.id)
    }

    const quickActions = [
        {
            title: "Join UpNComer",
            description: "Achieve your music dreams today!",
            icon: <Zap className="w-6 h-6" />,
            link: "/signup",
            color: "from-[#FA6138] to-[#e55532]",
        },
        {
            title: "Watch Demo Video",
            description: "Why We Built UpNComer",
            icon: <Video className="w-6 h-6" />,
            onClick: handleWatchDemo,
            color: "from-[#6366f1] to-[#4f46e5]",
        },
        {
            title: "Join Community",
            description: "Connect with other artists",
            icon: <Users className="w-6 h-6" />,
            link: "https://discord.gg/UAfektDzJd",
            openInNewTab: true,
            color: "from-[#818cf8] to-[#6366f1]",
        },
    ]

    //SEO
    const metadata = generateMetadata({
        title:
            "Help Center & Support | UpNComer",

        description:
            "Find guides and troubleshooting resources for music distribution, AI marketing, artist analytics, mastering, account management and platform features. UpNComer’s Help Center helps independent artists resolve issues, understand workflows and get the most from their music growth tools and services.",

        image: PAGE_OG_IMAGES.helpCenter,

        path: "/help-center",
    })

    const webPageSchema = generateWebPageSchema({
        title:
            "Help Center & Support | UpNComer",

        description:
            "Find guides and troubleshooting resources for music distribution, AI marketing, artist analytics, mastering, account management and platform features. UpNComer’s Help Center helps independent artists resolve issues, understand workflows and get the most from their music growth tools and services.",

        url: `${SITE_URL}/help-center`,
    })

    const breadcrumbItems = [
        {
            "name": "Home",
            "path": "/"
        },
        {
            "name": "Help Center",
            "path": "/help-center"
        }
    ]

    const schemas = generateGraphSchema([
        generateFAQPageSchema(),
        webPageSchema,
        generateBreadcrumbSchema(breadcrumbItems)
    ])

    return (
        <>
            {/* {isLoading && <HomepageLoader />} */}
            <SeoHead
                metadata={metadata}
                schemas={schemas}
            />
            <div
                className={`cursor-spotlight ${isHovering ? "hovering" : ""}`}
                style={{
                    left: `${cursorPosition.x}px`,
                    top: `${cursorPosition.y}px`,
                    transform: `translate(-50%, -50%)`,
                }}
            >
                <div className="inner-circle"></div>
            </div>
            <div
                className="transition-all duration-1000 opacity-100 blur-none"
            >
                <div className="relative min-h-screen bg-gradient-to-br from-[#000456] via-[#00067E] via-[#0a1a8a] to-[#1e3a8a] overflow-hidden">
                    {/* Enhanced Multi-layered Background */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute inset-0 bg-gradient-to-br from-[#000322] via-[#00067E] via-[#0a1a8a] via-[#1e3a8a] to-[#3b82f6] opacity-90" />

                        {/* Floating orbs */}
                        <motion.div
                            animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.3, 0.6, 0.3],
                            }}
                            transition={{
                                duration: 10,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                            }}
                            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-[#6366f1]/20 via-[#4f46e5]/15 to-[#FA6138]/10 rounded-full blur-3xl"
                        />

                        <motion.div
                            animate={{
                                scale: [1, 1.3, 1],
                                opacity: [0.2, 0.5, 0.2],
                            }}
                            transition={{
                                duration: 12,
                                repeat: Number.POSITIVE_INFINITY,
                                ease: "easeInOut",
                                delay: 3,
                            }}
                            className="absolute bottom-32 left-16 w-72 h-72 bg-gradient-to-br from-[#FA6138]/12 via-[#818cf8]/15 to-[#6366f1]/18 rounded-full blur-3xl"
                        />
                    </div>

                    <main className="relative z-10 mt-16 md:mt-18 py-16 md:py-20">
                        <div className="container max-w-6xl mx-auto">
                            {/* Header */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                className="text-center mb-16"
                            >
                                <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#c7d2fe] via-[#a5b4fc] to-[#818cf8] bg-clip-text text-transparent mb-6 drop-shadow-lg">
                                    Help Center
                                </h1>
                                <p className="bg-gradient-to-r from-[#818cf8]/80 via-[#6366f1]/85 to-[#4f46e5]/80 bg-clip-text text-transparent text-xl font-light max-w-3xl mx-auto leading-relaxed mb-8">
                                    Get the help you need to grow your music career with UpNComer
                                </p>

                                {/* Search Bar */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.8, delay: 0.2 }}
                                    className="relative max-w-2xl mx-auto"
                                >
                                    <label className="flex items-center gap-3 w-full px-4 py-4 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl backdrop-blur-xl focus-within:ring-2 focus-within:ring-[#FA6138]/50 focus-within:border-[#FA6138]/50">
                                        <Search
                                            aria-hidden="true"
                                            className="h-5 w-5 shrink-0 text-[#3b82f6]"
                                            stroke="#3b82f6"
                                        />
                                        <input
                                            type="text"
                                            placeholder="Search for help guides..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full bg-transparent border-0 p-0 text-[#c7d2fe] placeholder-[#818cf8]/60 focus:outline-none"
                                        />
                                    </label>

                                    {isSearching && (
                                        <div className="mt-4 text-left">
                                            {isSearchLoading ? (
                                                <div className="flex items-center justify-center min-h-32 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl backdrop-blur-xl">
                                                    <MusicLoader color="bg-[#FA6138]" />
                                                </div>
                                            ) : searchedGuides.length === 0 ? (
                                                <div className="bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-6 text-center backdrop-blur-xl">
                                                    <p className="text-[#93c5fd]">No guides matched your search.</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <p className="text-sm text-[#93c5fd]/80 mb-3">
                                                        Search results{searchQuery.trim() ? ` for "${searchQuery.trim()}"` : ""}
                                                    </p>
                                                    <div className="space-y-3">
                                                        {searchedGuides.map((guide) => (
                                                            <button
                                                                key={guide.id}
                                                                type="button"
                                                                onClick={() => handleGuideSelect(guide)}
                                                                className="w-full text-left bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-4 hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 transition-all duration-300 backdrop-blur-xl group"
                                                            >
                                                                <div className="flex items-center justify-between gap-3">
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center flex-wrap gap-2 mb-1">
                                                                            <h3 className="text-base font-semibold text-[#c7d2fe] group-hover:text-[#a5b4fc] transition-colors">
                                                                                {guide.title}
                                                                            </h3>
                                                                            {getGuideLabels(guide).map((label) => (
                                                                                <span key={label} className="text-xs px-2 py-0.5 rounded-full bg-[#FA6138]/15 text-[#FA6138] border border-[#FA6138]/30">
                                                                                    {label}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                        {guide.description && (
                                                                            <p className="text-[#818cf8]/70 text-sm line-clamp-2">{guide.description}</p>
                                                                        )}
                                                                    </div>
                                                                    <ChevronRight className="w-5 h-5 text-[#818cf8]/60 group-hover:text-[#FA6138] shrink-0" />
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                    {searchTotal > ALL_TOPICS_PAGE_SIZE && (
                                                        <div className="flex items-center justify-center gap-4 mt-4">
                                                            <button
                                                                type="button"
                                                                onClick={() => setSearchPage((page) => Math.max(0, page - 1))}
                                                                disabled={currentSearchPage === 0}
                                                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm backdrop-blur-xl transition-all ${currentSearchPage === 0
                                                                        ? "border-[#3b82f6]/20 text-[#64748b] cursor-not-allowed"
                                                                        : "border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:text-[#FA6138]"
                                                                    }`}
                                                            >
                                                                <ChevronLeft className="w-4 h-4" />
                                                                Prev
                                                            </button>
                                                            <span className="text-xs text-[#93c5fd]/80">
                                                                Page {currentSearchPage + 1} of {searchPageCount}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setSearchPage((page) => Math.min(searchPageCount - 1, page + 1))}
                                                                disabled={currentSearchPage >= searchPageCount - 1}
                                                                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-sm backdrop-blur-xl transition-all ${currentSearchPage >= searchPageCount - 1
                                                                        ? "border-[#3b82f6]/20 text-[#64748b] cursor-not-allowed"
                                                                        : "border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:text-[#FA6138]"
                                                                    }`}
                                                            >
                                                                Next
                                                                <ChevronRight className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </motion.div>
                            </motion.div>

                            {/* Quick Actions */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.4 }}
                                className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
                            >
                                {quickActions.map((action, index) => {
                                    const className = `bg-gradient-to-r ${action.color} p-6 rounded-2xl text-white hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl group`
                                    const content = (
                                        <>
                                            <div className="flex items-center mb-3">
                                                <div className="p-2 bg-white/20 rounded-lg mr-3 group-hover:bg-white/30 transition-colors">
                                                    {action.icon}
                                                </div>
                                                <div>
                                                    <h3 className="font-semibold">{action.title}</h3>
                                                    <p className="text-sm opacity-90">{action.description}</p>
                                                </div>
                                            </div>
                                            <ChevronRight className="w-5 h-5 ml-auto group-hover:translate-x-1 transition-transform" />
                                        </>
                                    )

                                    if (action.onClick) {
                                        return (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={action.onClick}
                                                className={`${className} w-full text-left`}
                                            >
                                                {content}
                                            </button>
                                        )
                                    }

                                    return (
                                        <a
                                            key={index}
                                            href={action.link}
                                            className={className}
                                            {...(action.openInNewTab
                                                ? { target: "_blank", rel: "noopener noreferrer" }
                                                : {})}
                                        >
                                            {content}
                                        </a>
                                    )
                                })}
                            </motion.div>

                            {/* Categories */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.6 }}
                                className="mb-12"
                            >
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                    Browse by Category
                                </h2>
                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                                    {categories.map((category) => (
                                        <div key={category.id} className="relative group h-full">
                                            <button
                                                type="button"
                                                onClick={() => handleCategorySelect(category.id, category.disabled)}
                                                className={`w-full h-full min-h-[88px] p-4 rounded-xl text-left transition-all duration-300 ${!category.disabled && selectedCategory === category.id
                                                        ? "bg-gradient-to-br from-[#FA6138]/20 to-[#e55532]/10 border border-[#FA6138]/30 text-[#FA6138]"
                                                        : "bg-[#1e3a8a]/20 border border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40"
                                                    } backdrop-blur-xl`}
                                            >
                                                <div className="flex items-center mb-2 min-w-0">
                                                    <span className="shrink-0">{category.icon}</span>
                                                    <span
                                                        title={category.label}
                                                        className="ml-2 font-medium text-xs whitespace-nowrap truncate flex-1 min-w-0"
                                                    >
                                                        {category.label}
                                                    </span>
                                                    {category.disabled && <Wrench className="w-4 h-4 shrink-0 ml-1" />}
                                                </div>
                                                <span className="text-xs opacity-70 whitespace-nowrap">
                                                    {category.count} {category.count === 1 ? "guide" : "guides"}
                                                </span>
                                            </button>
                                            {category.disabled && (
                                                <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full hidden group-hover:flex items-center justify-center bg-black text-white text-xs px-3 py-1.5 rounded-lg shadow-lg z-50 whitespace-nowrap">
                                                    Work In Progress
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </motion.div>

                            {/* Guides list or in-page viewer */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.8 }}
                            >
                                <h2 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-6">
                                    {isDemoGuide
                                        ? "Demo Video"
                                        : selectedCategory === "all"
                                            ? "All Guides"
                                            : `${categories.find((c) => c.id === selectedCategory)?.label} Guides`}
                                </h2>

                                {selectedGuide ? (
                                    <HelpGuideViewer
                                        guides={viewerGuides}
                                        currentId={selectedGuide.id}
                                        onChange={handleGuideSelect}
                                        onBack={() => setSelectedGuideId(null)}
                                    />
                                ) : isGuidesLoading ? (
                                    <div className="flex items-center justify-center min-h-48">
                                        <MusicLoader color="bg-[#FA6138]" />
                                    </div>
                                ) : filteredGuides.length === 0 ? (
                                    <div className="bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-8 text-center backdrop-blur-xl">
                                        <FileText className="w-10 h-10 text-[#818cf8]/70 mx-auto mb-3" />
                                        <p className="text-[#93c5fd]">No guides available in this category yet.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="space-y-4">
                                            {listedGuides.map((guide, index) => (
                                                <motion.button
                                                    key={guide.id}
                                                    type="button"
                                                    onClick={() => handleGuideSelect(guide)}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ duration: 0.5, delay: Math.min(index * 0.05, 0.4) }}
                                                    className="w-full text-left bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl p-6 hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 transition-all duration-300 backdrop-blur-xl group"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center mb-2 flex-wrap gap-2">
                                                                <h3 className="text-lg font-semibold text-[#c7d2fe] group-hover:text-[#a5b4fc] transition-colors">
                                                                    {guide.title}
                                                                </h3>
                                                                {getGuideLabels(guide).map((label) => (
                                                                    <span key={label} className="text-xs px-2 py-0.5 rounded-full bg-[#FA6138]/15 text-[#FA6138] border border-[#FA6138]/30">
                                                                        {label}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            {guide.description && (
                                                                <p className="text-[#818cf8]/70 text-sm mb-3">{guide.description}</p>
                                                            )}
                                                            {guide.readTime && (
                                                                <div className="flex items-center gap-4 text-xs text-[#60a5fa]/60">
                                                                    <span className="flex items-center">
                                                                        <Clock className="w-3 h-3 mr-1" />
                                                                        {guide.readTime}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <ChevronRight className="w-5 h-5 text-[#818cf8]/60 group-hover:text-[#FA6138] group-hover:translate-x-1 transition-all flex-shrink-0 ml-4" />
                                                    </div>
                                                </motion.button>
                                            ))}
                                        </div>
                                        {isPaginatedCategory && filteredGuides.length > ALL_TOPICS_PAGE_SIZE && (
                                            <div className="flex items-center justify-center gap-6 mt-8">
                                                <button
                                                    type="button"
                                                    onClick={() => setAllTopicsPage((page) => Math.max(0, page - 1))}
                                                    disabled={currentAllTopicsPage === 0}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border backdrop-blur-xl transition-all ${currentAllTopicsPage === 0
                                                            ? "border-[#3b82f6]/20 text-[#64748b] cursor-not-allowed"
                                                            : "border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 hover:text-[#FA6138]"
                                                        }`}
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                    Prev
                                                </button>
                                                <span className="text-sm text-[#93c5fd]/80">
                                                    Page {currentAllTopicsPage + 1} of {allTopicsPageCount}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setAllTopicsPage((page) => Math.min(allTopicsPageCount - 1, page + 1))}
                                                    disabled={currentAllTopicsPage >= allTopicsPageCount - 1}
                                                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border backdrop-blur-xl transition-all ${currentAllTopicsPage >= allTopicsPageCount - 1
                                                            ? "border-[#3b82f6]/20 text-[#64748b] cursor-not-allowed"
                                                            : "border-[#3b82f6]/30 text-[#93c5fd] hover:bg-[#3b82f6]/20 hover:border-[#60a5fa]/40 hover:text-[#FA6138]"
                                                        }`}
                                                >
                                                    Next
                                                    <ChevronRight className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </motion.div>

                            {/* Contact Support */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 1.2 }}
                                className="mt-16 text-center"
                            >
                                <div
                                    className="bg-gradient-to-br from-[#312e81]/20 via-[#1e1b4b]/15 to-[#0f0f23]/25 backdrop-blur-xl border border-[#6366f1]/20 rounded-2xl p-8"
                                    style={{
                                        boxShadow: "0 20px 40px rgba(99, 102, 241, 0.25), inset 0 1px 0 rgba(165, 180, 252, 0.15)",
                                    }}
                                >
                                    <HelpCircle className="w-12 h-12 text-[#FA6138] mx-auto mb-4" />
                                    <h3 className="text-2xl font-bold bg-gradient-to-r from-[#c7d2fe] to-[#a5b4fc] bg-clip-text text-transparent mb-4">
                                        Still Need Help?
                                    </h3>
                                    <p className="text-[#818cf8]/80 mb-6 max-w-2xl mx-auto">
                                        Can't find what you're looking for? Our support team is here to help you succeed with UpNComer.
                                    </p>
                                    <button
                                        onClick={openSupportChat}
                                        className="bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white px-8 py-3 rounded-xl hover:from-[#e55532] hover:to-[#FA6138] transition-all duration-300 font-medium shadow-lg hover:shadow-xl"
                                    >
                                        Contact Support Team
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </main>

                    <AnimatePresence>
                        {!showSupportChat && (
                            <motion.button
                                type="button"
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.8, opacity: 0 }}
                                transition={{ type: "spring", stiffness: 320, damping: 22 }}
                                onClick={openSupportChat}
                                aria-label="Chat with UpNComer Support"
                                className="fixed bottom-6 right-6 z-50 group flex items-center"
                            >
                                <span className="absolute right-full mr-3 whitespace-nowrap rounded-full bg-[#0f172a]/90 border border-white/10 px-3 py-1.5 text-xs font-medium text-[#e2e8f0] opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200 pointer-events-none shadow-lg">
                                    Chat with us
                                </span>
                                <span
                                    className="relative w-16 h-16 rounded-full bg-gradient-to-br from-[#FA6138] via-[#f5673f] to-[#d94a2a] text-white flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                                    style={{
                                        boxShadow: "0 10px 28px rgba(250, 97, 56, 0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                                    }}
                                >
                                    <span className="absolute inset-0 rounded-full bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    <MessageCircle className="w-7 h-7 relative z-10" />
                                    <span className="absolute bottom-1 right-1 z-20 flex h-4 w-4">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                                        <span className="relative inline-flex h-4 w-4 rounded-full bg-emerald-400 border-2 border-white shadow-[0_0_0_1px_rgba(16,185,129,0.35)]" />
                                    </span>
                                </span>
                            </motion.button>
                        )}
                    </AnimatePresence>

                    {/* Support Chat Modal */}
                    <AnimatePresence>
                        {showSupportChat && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end justify-end p-4 sm:p-6"
                                onClick={closeSupportChat}
                            >
                                <motion.div
                                    initial={{ opacity: 0, y: 100, scale: 0.9 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 100, scale: 0.9 }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-full max-w-md h-[32rem] bg-gradient-to-br from-[#1e1b4b]/95 via-[#151338]/95 to-[#0b1024]/98 backdrop-blur-xl border border-[#6366f1]/30 rounded-2xl overflow-hidden flex flex-col"
                                    style={{
                                        boxShadow: "0 25px 50px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(165, 180, 252, 0.2)",
                                    }}
                                >
                                    <div className="bg-gradient-to-r from-[#FA6138] to-[#e55532] px-4 py-3.5 flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div className="relative w-10 h-10 bg-white/20 rounded-full flex items-center justify-center mr-3">
                                                <MessageCircle className="w-5 h-5 text-white" />
                                                <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3">
                                                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-70 animate-ping" />
                                                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400 border-2 border-white" />
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="text-white font-semibold leading-tight">UpNComer Support</h3>
                                                <p className="text-white/80 text-xs flex items-center gap-1.5">
                                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300" />
                                                    Typically replies by email
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={closeSupportChat}
                                            className="text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
                                            aria-label="Close support chat"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="flex-1 p-4 overflow-y-auto space-y-4">
                                        {chatMessages.map((message) => (
                                            <div
                                                key={message.id}
                                                className={`flex items-end gap-2 ${message.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                                            >
                                                {message.role === "support" ? (
                                                    <div className="w-8 h-8 bg-[#FA6138] rounded-full flex items-center justify-center flex-shrink-0">
                                                        <MessageCircle className="w-4 h-4 text-white" />
                                                    </div>
                                                ) : (
                                                    <div className="w-8 h-8 bg-[#3b82f6]/40 rounded-full flex items-center justify-center flex-shrink-0 text-[#c7d2fe] text-xs font-semibold">
                                                        You
                                                    </div>
                                                )}
                                                <div className={`max-w-[78%] ${message.role === "user" ? "items-end" : "items-start"} flex flex-col`}>
                                                    <div
                                                        className={`rounded-2xl p-3 ${
                                                            message.role === "user"
                                                                ? "bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white rounded-br-md"
                                                                : "bg-[#1e3a8a]/40 border border-[#6366f1]/20 text-[#c7d2fe] rounded-bl-md"
                                                        }`}
                                                    >
                                                        <p className="text-sm leading-relaxed">{message.text}</p>
                                                    </div>
                                                    {message.showGuestForm && (
                                                        <form
                                                            className="mt-2 w-full space-y-2"
                                                            onSubmit={(e) => {
                                                                e.preventDefault()
                                                                handleGuestContactSubmit()
                                                            }}
                                                        >
                                                            <input
                                                                type="text"
                                                                value={guestContact.firstName}
                                                                onChange={(e) => {
                                                                    setGuestContact((prev) => ({ ...prev, firstName: e.target.value }))
                                                                    if (contactError) setContactError("")
                                                                }}
                                                                disabled={chatStep !== "guest-contact"}
                                                                placeholder="First name"
                                                                className="w-full px-3 py-2 bg-[#0f172a]/80 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] placeholder-[#818cf8]/60 text-sm focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 disabled:opacity-50"
                                                            />
                                                            <input
                                                                type="text"
                                                                value={guestContact.lastName}
                                                                onChange={(e) => {
                                                                    setGuestContact((prev) => ({ ...prev, lastName: e.target.value }))
                                                                    if (contactError) setContactError("")
                                                                }}
                                                                disabled={chatStep !== "guest-contact"}
                                                                placeholder="Last name"
                                                                className="w-full px-3 py-2 bg-[#0f172a]/80 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] placeholder-[#818cf8]/60 text-sm focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 disabled:opacity-50"
                                                            />
                                                            <input
                                                                type="email"
                                                                value={guestContact.email}
                                                                onChange={(e) => {
                                                                    setGuestContact((prev) => ({ ...prev, email: e.target.value }))
                                                                    if (contactError) setContactError("")
                                                                }}
                                                                disabled={chatStep !== "guest-contact"}
                                                                placeholder="Email address"
                                                                className="w-full px-3 py-2 bg-[#0f172a]/80 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] placeholder-[#818cf8]/60 text-sm focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 disabled:opacity-50"
                                                            />
                                                            <button
                                                                type="submit"
                                                                disabled={chatStep !== "guest-contact"}
                                                                className="w-full px-3 py-2 bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white rounded-xl text-sm font-medium disabled:opacity-50"
                                                            >
                                                                Continue
                                                            </button>
                                                            {contactError && chatStep === "guest-contact" && (
                                                                <p className="text-xs text-rose-300">{contactError}</p>
                                                            )}
                                                        </form>
                                                    )}
                                                    {message.showTopics && (
                                                        <div className="mt-2 w-full">
                                                            <select
                                                                defaultValue=""
                                                                disabled={chatStep !== "topic"}
                                                                onChange={(e) => {
                                                                    const topic = SUPPORT_TOPICS.find((item) => item.value === e.target.value)
                                                                    if (topic) handleTopicSelect(topic)
                                                                }}
                                                                className="w-full px-3 py-2 bg-[#0f172a]/80 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] text-sm focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 disabled:opacity-50"
                                                            >
                                                                <option value="" disabled>Select a topic</option>
                                                                {SUPPORT_TOPICS.map((topic) => (
                                                                    <option key={topic.value} value={topic.value}>
                                                                        {topic.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                    <span className="text-[#818cf8]/60 text-[11px] mt-1 px-1">{message.time}</span>
                                                </div>
                                            </div>
                                        ))}
                                        {sendSupport.isPending && (
                                            <div className="flex items-center gap-2 text-[#818cf8] text-xs pl-10">
                                                <span className="w-1.5 h-1.5 rounded-full bg-[#FA6138] animate-pulse" />
                                                Sending your request...
                                            </div>
                                        )}
                                        <div ref={chatEndRef} />
                                    </div>

                                    <div className="p-4 border-t border-[#6366f1]/20 bg-[#0f0f23]/40">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                placeholder={
                                                    chatStep === "guest-contact"
                                                        ? "Fill in your details above..."
                                                        : chatStep === "topic"
                                                            ? "Select a topic above..."
                                                            : chatStep === "custom-topic"
                                                                ? "Type your topic..."
                                                                : chatStep === "complete"
                                                                    ? "We've received your request"
                                                                    : "Type your message..."
                                                }
                                                value={chatMessage}
                                                onChange={(e) => setChatMessage(e.target.value)}
                                                disabled={["guest-contact", "topic", "complete"].includes(chatStep) || sendSupport.isPending}
                                                className="flex-1 px-3 py-2.5 bg-[#1e3a8a]/20 border border-[#3b82f6]/30 rounded-xl text-[#c7d2fe] placeholder-[#818cf8]/60 focus:outline-none focus:ring-2 focus:ring-[#FA6138]/50 text-sm disabled:opacity-50"
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" && chatMessage.trim()) {
                                                        handleSendChatMessage()
                                                    }
                                                }}
                                            />
                                            <button
                                                onClick={handleSendChatMessage}
                                                disabled={!chatMessage.trim() || ["guest-contact", "topic", "complete"].includes(chatStep) || sendSupport.isPending}
                                                className="p-2.5 bg-gradient-to-r from-[#FA6138] to-[#e55532] text-white rounded-xl hover:from-[#e55532] hover:to-[#FA6138] transition-all duration-300 disabled:opacity-40 disabled:hover:from-[#FA6138] disabled:hover:to-[#e55532]"
                                                aria-label="Send message"
                                            >
                                                <Send className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {contactError && chatStep !== "guest-contact" && (
                                            <p className="mt-2 text-xs text-rose-300">{contactError}</p>
                                        )}
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </>
    )
}
